// ─────────────────────────────────────────────────────────────────────────────
// pioneerStore.ts — INFINI Pioneer Registration
// Base: GP1 (final wired store) + constants from CP2 adapted to GP1 tier names
// ─────────────────────────────────────────────────────────────────────────────

import { create } from 'zustand'
import { devtools, persist } from 'zustand/middleware'

// ─────────────────────────────────────────────
// Types
// ─────────────────────────────────────────────

export type Screen = 1 | 2 | 3 | 4 | 5

export type ZodiacSign =
  | 'Aries' | 'Taurus' | 'Gemini' | 'Cancer' | 'Leo' | 'Virgo'
  | 'Libra' | 'Scorpio' | 'Sagittarius' | 'Capricorn' | 'Aquarius' | 'Pisces'

export interface BirthData {
  /** User's preferred display name */
  name: string
  /** YYYY-MM-DD */
  birthDate: string
  /** HH:MM in 24-hour format */
  birthTime: string
  /** Human-readable place string for UI display */
  birthPlace: string
  /** Decimal latitude, e.g. 9.1382 */
  lat: number
  /** Decimal longitude, e.g. 99.3214 */
  lng: number
  /** IANA timezone string, e.g. "Asia/Bangkok" */
  timezone: string
}

export interface PlanetPlacement {
  planet: string
  sign: ZodiacSign
  degree: number
  house: number
  retrograde: boolean
  element: 'Fire' | 'Earth' | 'Air' | 'Water'
  modality?: 'Cardinal' | 'Fixed' | 'Mutable'
}

export interface HouseCusp {
  house: number
  sign: string
  degree: number
}

export interface Aspect {
  planet1: string
  planet2: string
  aspect: string
  orb: number
}

/** Extended planet object — used by CP2 screens (sun/moon/rising on ChartData) */
export interface ChartPlanet {
  sign: string
  degree: number
  house?: number
  /** Unicode zodiac glyph e.g. '♈' — added by Screen2's mock + real ephemeris */
  glyph?: string
  element?: 'Fire' | 'Earth' | 'Air' | 'Water'
  modality?: 'Cardinal' | 'Fixed' | 'Mutable'
  description?: string
}

/** Shape returned by /api/calculate-chart (Swiss Ephemeris) */
export interface ChartData {
  sun: ChartPlanet
  moon: ChartPlanet
  rising: ChartPlanet | null
  placements: PlanetPlacement[]
  houses: HouseCusp[]
  aspects: Aspect[]
  /** Raw ephemeris timestamp for cache validation */
  calculatedAt?: string
}

export type PaymentStatus =
  | 'idle'
  | 'pending'       // user clicked Pay
  | 'processing'    // Stripe webhook in flight
  | 'success'       // payment confirmed
  | 'failed'        // terminal failure

export type PioneerTier = 'STELLIUM' | 'LUMINARY' | 'CELESTIAL'

export const TIER_PRICE_THB: Record<PioneerTier, number> = {
  STELLIUM:  4900,
  LUMINARY:  2900,
  CELESTIAL: 990,
}

export interface PioneerSeat {
  /** e.g. "INFINI-0042" */
  serialNumber: string
  seatNumber: number
  tier: PioneerTier
  /** ISO timestamp set by Supabase assign-pioneer-seat function */
  assignedAt: string
  userId: string
}

// ─────────────────────────────────────────────
// Tier Config (from CP2, adapted to GP1 tier names)
// ─────────────────────────────────────────────

export const TIER_CONFIG: Record<PioneerTier, {
  label: string
  seatRange: string
  seats: [number, number]
  price: number   // THB
  perks: string[]
  tagline: string
}> = {
  STELLIUM: {
    label: 'Stellium',
    seatRange: '001 – 088',
    seats: [1, 88],
    price: 4900,
    tagline: 'First light. First name.',
    perks: [
      'Permanent seat #001–088',
      'Stellium physical Name Card',
      'Lifetime all-access (all future gacha packs)',
      'Founding Council private channel',
      'One-to-one cosmic strategy session',
      "Your name in INFINI's origin story",
    ],
  },
  LUMINARY: {
    label: 'Luminary',
    seatRange: '089 – 388',
    seats: [89, 388],
    price: 2900,
    tagline: 'You were here before the world knew.',
    perks: [
      'Permanent seat #089–388',
      'Luminary physical Name Card',
      '12 months Orbit subscription',
      'Pioneer badge on every profile',
      'Early access to all new features',
    ],
  },
  CELESTIAL: {
    label: 'Celestial',
    seatRange: '389 – 888',
    seats: [389, 888],
    price: 990,
    tagline: 'Among the first 888.',
    perks: [
      'Permanent seat #389–888',
      'Celestial digital Name Card',
      '3 months Orbit subscription',
      'Pioneer badge on every profile',
      'Priority access to new features',
    ],
  },
}

export function getTierForSeat(seat: number): PioneerTier {
  if (seat <= 88)  return 'STELLIUM'
  if (seat <= 388) return 'LUMINARY'
  return 'CELESTIAL'
}

export function formatSerial(seat: number): string {
  return `INFINI-${String(seat).padStart(4, '0')}`
}

// ─────────────────────────────────────────────
// Zodiac Helpers (from CP2)
// ─────────────────────────────────────────────

export const ZODIAC_GLYPHS: Record<ZodiacSign, string> = {
  Aries: '♈', Taurus: '♉', Gemini: '♊', Cancer: '♋',
  Leo: '♌', Virgo: '♍', Libra: '♎', Scorpio: '♏',
  Sagittarius: '♐', Capricorn: '♑', Aquarius: '♒', Pisces: '♓',
}

export const ZODIAC_ELEMENTS: Record<ZodiacSign, PlanetPlacement['element']> = {
  Aries: 'Fire', Leo: 'Fire', Sagittarius: 'Fire',
  Taurus: 'Earth', Virgo: 'Earth', Capricorn: 'Earth',
  Gemini: 'Air', Libra: 'Air', Aquarius: 'Air',
  Cancer: 'Water', Scorpio: 'Water', Pisces: 'Water',
}

export const ELEMENT_COLORS: Record<PlanetPlacement['element'], string> = {
  Fire:  '#D4663A',
  Earth: '#7A9E5F',
  Air:   '#7ABBE0',
  Water: '#5F7FA8',
}

// ─────────────────────────────────────────────
// Store interface
// ─────────────────────────────────────────────

export interface PioneerStore {
  // ── Navigation ─────────────────────────────
  currentScreen: Screen
  setScreen: (screen: Screen) => void
  goNext: () => void
  goBack: () => void

  // ── Screen 2: Birth Data ───────────────────
  birthData: BirthData | null
  /** Accepts a full or partial BirthData — merges with existing state */
  setBirthData: (data: Partial<BirthData>) => void

  // ── Screen 3: Chart Reading ────────────────
  chartData: ChartData | null
  chartLoading: boolean
  chartError: string | null
  calculateChart: () => Promise<void>
  setChartData: (data: ChartData) => void
  clearChartError: () => void

  // ── Screen 4: Pioneer Offer ────────────────
  selectedTier: PioneerTier | null
  setSelectedTier: (tier: PioneerTier) => void
  paymentStatus: PaymentStatus
  paymentError: string | null
  stripeSessionId: string | null
  setPaymentStatus: (status: PaymentStatus) => void
  setPaymentError: (error: string | null) => void
  setStripeSessionId: (id: string) => void

  // ── Screen 5: Confirmation ─────────────────
  pioneerSeat: PioneerSeat | null
  setPioneerSeat: (seat: PioneerSeat) => void

  // ── Session ────────────────────────────────
  userId: string | null
  setUserId: (id: string) => void

  // ── Utilities ──────────────────────────────
  reset: () => void
}

// ─────────────────────────────────────────────
// Initial state
// ─────────────────────────────────────────────

const initialState = {
  currentScreen: 1 as Screen,
  birthData: null,
  chartData: null,
  chartLoading: false,
  chartError: null,
  selectedTier: null,
  paymentStatus: 'idle' as PaymentStatus,
  paymentError: null,
  stripeSessionId: null,
  pioneerSeat: null,
  userId: null,
}

// ─────────────────────────────────────────────
// Store
// ─────────────────────────────────────────────

export const usePioneerStore = create<PioneerStore>()(
  devtools(
    persist(
      (set, get) => ({
        ...initialState,

        // ── Navigation ──────────────────────────
        setScreen: (screen) =>
          set({ currentScreen: screen }, false, 'setScreen'),

        goNext: () => {
          const curr = get().currentScreen
          if (curr < 5) set({ currentScreen: (curr + 1) as Screen }, false, 'goNext')
        },

        goBack: () => {
          const curr = get().currentScreen
          if (curr > 1) set({ currentScreen: (curr - 1) as Screen }, false, 'goBack')
        },

        // ── Birth Data ──────────────────────────
        setBirthData: (data) =>
          set(
            (s) => ({ birthData: { ...(s.birthData ?? {}), ...data } as BirthData }),
            false,
            'setBirthData'
          ),

        // ── Chart ───────────────────────────────
        setChartData: (data) =>
          set({ chartData: data }, false, 'setChartData'),

        clearChartError: () =>
          set({ chartError: null }, false, 'clearChartError'),

        calculateChart: async () => {
          const { birthData } = get()

          if (!birthData) {
            set({ chartError: 'Birth data missing — please complete form.' }, false, 'calculateChart/noData')
            return
          }

          set({ chartLoading: true, chartError: null }, false, 'calculateChart/start')

          try {
            const res = await fetch('/api/calculate-chart', {
              method: 'POST',
              headers: { 'Content-Type': 'application/json' },
              body: JSON.stringify({
                date:     birthData.birthDate,
                time:     birthData.birthTime,
                lat:      birthData.lat,
                lng:      birthData.lng,
                timezone: birthData.timezone,
              }),
            })

            if (!res.ok) {
              const errBody = await res.json().catch(() => ({}))
              throw new Error(
                (errBody as { message?: string }).message ?? `Server error (HTTP ${res.status})`
              )
            }

            const chartData: ChartData = await res.json()

            set(
              { chartData, chartLoading: false, currentScreen: 3 },
              false,
              'calculateChart/success'
            )
          } catch (e: unknown) {
            const msg = e instanceof Error ? e.message : 'Chart calculation failed. Please try again.'
            set({ chartError: msg, chartLoading: false }, false, 'calculateChart/error')
          }
        },

        // ── Payment ─────────────────────────────
        setSelectedTier:   (tier)   => set({ selectedTier: tier },    false, 'setSelectedTier'),
        setPaymentStatus:  (status) => set({ paymentStatus: status }, false, 'setPaymentStatus'),
        setPaymentError:   (error)  => set({ paymentError: error },   false, 'setPaymentError'),
        setStripeSessionId:(id)     => set({ stripeSessionId: id },   false, 'setStripeSessionId'),

        // ── Pioneer Seat ─────────────────────────
        setPioneerSeat: (seat) =>
          set({ pioneerSeat: seat }, false, 'setPioneerSeat'),

        // ── Session ─────────────────────────────
        setUserId: (id) =>
          set({ userId: id }, false, 'setUserId'),

        // ── Reset ────────────────────────────────
        reset: () =>
          set({ ...initialState, userId: get().userId }, false, 'reset'),
      }),
      {
        name: 'infini-pioneer-v1',
        partialize: (s) => ({
          currentScreen:  s.currentScreen,
          birthData:      s.birthData,
          chartData:      s.chartData,
          selectedTier:   s.selectedTier,
          pioneerSeat:    s.pioneerSeat,
          userId:         s.userId,
        }),
      }
    ),
    { name: 'PioneerStore' }
  )
)
